import { cn } from '@aibi/ui';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { conditionalStyleOf } from '../conditional-format';
import { channelColumns, effectiveFormat, formatValue } from '../result-access';
import type { RendererProps } from '../types';
import { kpiValueClassOf } from '../viz-style';

/**
 * Tarjeta KPI: una cifra grande por medida (hasta 4). El dato llena el cuerpo a ras
 * (sin padding) para permitir tarjetas muy compactas. Si el resultado trae una columna
 * de comparacion de variacion, muestra la tendencia.
 */
export function KpiRenderer({ spec, result, label }: RendererProps) {
  const measures = channelColumns(spec, result, 'values', 'measure').slice(0, 4);
  const row = result.rows[0];
  const variation = result.columns.findIndex(
    (column) => column.role === 'comparison' && column.format?.type === 'percent',
  );
  const valueClass = kpiValueClassOf(spec.style);
  const single = measures.length <= 1;

  return (
    <div
      role="group"
      aria-label={label}
      data-testid="kpi-renderer"
      className={cn(
        'grid h-full min-h-0 w-full',
        single ? 'grid-cols-1' : 'grid-cols-2 gap-px bg-border',
      )}
    >
      {measures.map(({ column, index }) => {
        const value = row?.[index] ?? null;
        const change = variation >= 0 ? (row?.[variation] ?? null) : null;
        const conditional = conditionalStyleOf(spec, column.key, value);
        return (
          <div
            key={column.key}
            className={cn(
              'flex min-h-0 min-w-0 flex-col items-center justify-center text-center',
              !single && 'bg-card',
            )}
            style={conditional}
          >
            {!single && (
              <span className="max-w-full truncate px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {column.label}
              </span>
            )}
            <span
              className={cn(
                'max-w-full truncate font-semibold tabular-nums tracking-tight leading-none',
                valueClass,
              )}
              aria-label={`${column.label}: ${formatValue(value, column, effectiveFormat(spec, column))}`}
            >
              {formatValue(value, column, effectiveFormat(spec, column), { compact: true })}
            </span>
            {typeof change === 'number' && (
              <span
                className={cn(
                  'mt-0.5 inline-flex max-w-full items-center gap-0.5 truncate text-[10px] font-medium',
                  change >= 0 ? 'text-success' : 'text-destructive',
                )}
              >
                {change >= 0 ? (
                  <TrendingUp aria-hidden className="size-3 shrink-0" />
                ) : (
                  <TrendingDown aria-hidden className="size-3 shrink-0" />
                )}
                {new Intl.NumberFormat('es', { style: 'percent', maximumFractionDigits: 1 }).format(
                  change,
                )}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
